// ─────────────────────────────────────────────────────────────────────────────
// LaneMenu — the agent's lane, in the chat bar (frontier lanes, Phase 4; owner
// rulings R1, R5, R8, R34). Automatic · Local · API · Sub, and for Sub the
// provider (Claude · GPT) and then the model. Sits beside the model pill and
// wears its look; today's controls are not touched.
//
// ALWAYS THE SERVER'S TRUTH, FOR THIS CHAT'S OWN AGENT. Read on mount, on every
// agent switch and whenever the chat's message count moves (a reply landed), so
// a Sub lane that fell back reads Automatic. Everything is keyed by the chat's
// own domain + agent; an answer that arrives after the chat moved to another
// agent is dropped, never shown.
//
// Shown only for an agent the server lists as eligible (never SECURITY, CODE,
// JOINT or a not-built agent). Nothing renders until the server has answered.
// API is listed and saved as it is, with no menu of its own (R34). Sub is saved
// only once a model is picked; a pick POSTs and the pill shows what the server
// stored.
//
// INSIDE THE WINDOW (Round 6). The menu and its Sub → provider → model steps are
// placed from the pill's own position: under its right edge as before when that
// fits, else under its left edge, else held a margin inside the window (a narrow
// window narrows the menu too), and never taller than the room above the pill.
//
// THE MODEL CHOICE (R42). Where the lane pill shows it is the chat's model
// choice; onVisibility tells the chat bar whether it shows, so the bar keeps its
// older model pill only where there is no lane pill.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  chatKey,
  getEligible,
  getLane,
  getModels,
  isEligible,
  setLane,
  type EligibleAgent,
  type Lane,
  type LaneModel,
  type LaneState,
  type SubProvider,
} from '../api/agentLane';

const LANE_LABEL: Record<Lane, string> = {
  automatic: 'Automatic',
  local: 'Local',
  api: 'API',
  sub: 'Sub',
};

const PROVIDER_LABEL: Record<SubProvider, string> = { claude: 'Claude', gpt: 'GPT' };

function pillLabel(s: LaneState): string {
  if (s.lane === 'sub') {
    const provider = PROVIDER_LABEL[s.provider as SubProvider] || s.provider;
    return `SUB · ${provider}`;
  }
  return s.lane === 'automatic' ? 'AUTO' : LANE_LABEL[s.lane];
}

function dotColor(s: LaneState): string {
  if (s.lane === 'local') return 'bg-green-500';
  if (s.lane === 'api') return 'bg-blue-400';
  if (s.lane === 'sub') return s.provider === 'gpt' ? 'bg-emerald-400' : 'bg-blue-400';
  return 'bg-rexy-muted';
}

/** Whether the lane pill shows for this chat: not yet known, shown, or not shown. */
export type LaneVisibility = 'pending' | 'shown' | 'hidden';

interface LaneMenuProps {
  domain?: string | null;
  agent?: string | null;
  /** Moves whenever the chat's messages do — a reply landed: re-read the lane. */
  turnCount?: number;
  /** Told whenever whether the lane pill shows changes (R42). */
  onVisibility?: (visibility: LaneVisibility) => void;
}

type Step = 'closed' | 'lane' | 'provider' | 'model';

/** The menu's width (w-60) and the margin it keeps from the window's edges. */
export const MENU_WIDTH = 240;
export const EDGE = 8;

export interface MenuPlace {
  /** px from the pill's left edge to the menu's left edge */
  left: number;
  width: number;
  maxHeight: number;
}

/**
 * Where the menu sits so all of it stays inside a window `viewport` px wide:
 * right-aligned to the pill when that fits (today's place), else left-aligned,
 * else held EDGE px inside the window's left edge. A window narrower than the
 * menu plus both margins narrows it. It opens upward, so its height is the room
 * above the pill.
 */
export function placeMenu(pill: { left: number; right: number; top: number }, viewport: number): MenuPlace {
  const width = Math.max(0, Math.min(MENU_WIDTH, viewport - 2 * EDGE));
  const lo = EDGE;
  const hi = viewport - EDGE - width;      // the window x where the menu's left edge may sit
  const today = pill.right - width;        // under the pill's right edge
  let x;
  if (today >= lo && today <= hi) x = today;
  else if (pill.left >= lo && pill.left <= hi) x = pill.left;
  else x = Math.min(Math.max(today, lo), hi);
  return { left: x - pill.left, width, maxHeight: Math.max(120, Math.floor(pill.top - EDGE - 8)) };
}

const LaneMenu: React.FC<LaneMenuProps> = ({ domain, agent, turnCount, onVisibility }) => {
  const key = domain && agent ? chatKey(domain, agent) : '';
  const [state, setState] = useState<{ key: string; lane: LaneState | null }>({ key: '', lane: null });
  const [eligible, setEligible] = useState<EligibleAgent[] | null>(null);
  const [eligibleRead, setEligibleRead] = useState(false);
  const [step, setStep] = useState<Step>('closed');
  const [provider, setProvider] = useState<SubProvider | null>(null);
  const [models, setModels] = useState<LaneModel[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [place, setPlace] = useState<MenuPlace | null>(null);
  const current = useRef(key);
  const wrapper = useRef<HTMLDivElement>(null);
  current.current = key;

  // The eligible list: once per mount (the roster does not change under a chat).
  useEffect(() => {
    let live = true;
    getEligible().then((rows) => {
      if (live) {
        setEligible(rows);
        setEligibleRead(true);
      }
    });
    return () => { live = false; };
  }, []);

  // This chat's lane: on mount, on every agent switch, and after every reply.
  useEffect(() => {
    if (!key || !domain || !agent) return;
    let live = true;
    getLane(domain, agent).then((lane) => {
      if (live && current.current === key) setState({ key, lane });
    });
    return () => { live = false; };
  }, [key, domain, agent, turnCount]);

  // A switch of agent closes the menu and forgets the last agent's menu state.
  useEffect(() => {
    setStep('closed');
    setProvider(null);
    setModels(null);
    setError(null);
  }, [key]);

  // Close on an outside click (the overflow menu's pattern).
  useEffect(() => {
    if (step === 'closed') return;
    const close = (e: MouseEvent) => {
      if (wrapper.current && !wrapper.current.contains(e.target as Node)) setStep('closed');
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [step]);

  // Inside the window: placed from the pill before the open menu paints, and again
  // whenever the window is resized while it is open.
  useLayoutEffect(() => {
    if (step === 'closed') return;
    const measure = () => {
      if (wrapper.current) setPlace(placeMenu(wrapper.current.getBoundingClientRect(), window.innerWidth));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [step]);

  const shown = state.key === key ? state.lane : null;
  const visibility: LaneVisibility = !key || !domain || !agent ? 'hidden'
    : !eligibleRead || state.key !== key ? 'pending'
      : shown && isEligible(eligible, shown) ? 'shown' : 'hidden';
  useEffect(() => {
    if (onVisibility) onVisibility(visibility);
  }, [visibility, onVisibility]);

  if (!key || !domain || !agent || !shown || !isEligible(eligible, shown)) return null;

  const save = async (lane: Lane, subProvider = '', model = '') => {
    setSaving(true);
    setError(null);
    const asked = key;
    const result = await setLane(domain, agent, lane, subProvider, model);
    setSaving(false);
    if (current.current !== asked) return;
    if (result.state) {
      setState({ key: asked, lane: result.state });
      setStep('closed');
    } else {
      setError(result.error);
      // Whatever happened, show what the server now holds.
      const lane2 = await getLane(domain, agent);
      if (current.current === asked) setState({ key: asked, lane: lane2 });
    }
  };

  const openProvider = async (p: SubProvider) => {
    setProvider(p);
    setModels(null);
    setStep('model');
    const asked = key;
    const list = await getModels(p);
    if (current.current === asked) setModels(list || []);
  };

  const rowClass = (active: boolean) =>
    `w-full text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 transition-colors ${
      active ? 'bg-rexy-accent/20 text-rexy-accent' : 'text-rexy-muted hover:text-rexy-text hover:bg-rexy-border'
    }`;

  return (
    <div className="relative" ref={wrapper} onClick={(e) => e.stopPropagation()} data-testid="lane-menu">
      <button
        onClick={() => setStep((s) => (s === 'closed' ? 'lane' : 'closed'))}
        disabled={saving}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-rexy-border
          bg-rexy-dark hover:text-rexy-text text-rexy-muted text-[10px] font-bold uppercase tracking-widest
          transition-colors"
        title={shown.lane === 'sub'
          ? `This agent runs on Sub · ${PROVIDER_LABEL[shown.provider as SubProvider] || shown.provider} · ${shown.model}`
          : `This agent runs on ${LANE_LABEL[shown.lane]}`}
        data-testid="lane-pill"
      >
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor(shown)}`} />
        <span>{pillLabel(shown)}</span>
        <span className="text-rexy-muted/60 leading-none">›</span>
      </button>
      {step !== 'closed' && (
        <div className="absolute bottom-full mb-2 bg-rexy-card border border-rexy-border
          rounded-lg shadow-xl z-50 overflow-x-hidden overflow-y-auto" data-testid="lane-dropdown"
          style={place
            ? { left: place.left, width: place.width, maxHeight: place.maxHeight }
            : { right: 0, width: MENU_WIDTH }}>
          <div className="px-3 py-2 text-[9px] text-rexy-muted font-bold uppercase
            tracking-widest border-b border-rexy-border">
            {agent.toUpperCase()} LANE
            {step === 'model' && provider ? ` · SUB · ${PROVIDER_LABEL[provider]}` : ''}
            {step === 'provider' ? ' · SUB' : ''}
          </div>
          {step === 'lane' && LANES_IN_MENU.map((lane) => (
            <button
              key={lane}
              className={rowClass(shown.lane === lane)}
              onClick={() => (lane === 'sub' ? setStep('provider') : save(lane))}
              data-testid={`lane-option-${lane}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                shown.lane === lane ? 'bg-rexy-accent' : 'bg-rexy-muted'}`} />
              {LANE_LABEL[lane]}
              {lane === 'sub' && <span className="ml-auto text-rexy-muted/60">›</span>}
            </button>
          ))}
          {step === 'provider' && (['claude', 'gpt'] as SubProvider[]).map((p) => (
            <button
              key={p}
              className={rowClass(shown.lane === 'sub' && shown.provider === p)}
              onClick={() => openProvider(p)}
              data-testid={`lane-provider-${p}`}
            >
              {PROVIDER_LABEL[p]}
              <span className="ml-auto text-rexy-muted/60">›</span>
            </button>
          ))}
          {step === 'model' && models === null && (
            <div className="px-3 py-2 text-[10px] text-rexy-muted">Reading the model menu…</div>
          )}
          {step === 'model' && models !== null && models.length === 0 && (
            <div className="px-3 py-2 text-[10px] text-rexy-muted">The model menu could not be read.</div>
          )}
          {step === 'model' && provider && models && models.length > 0 && (
            <div className="max-h-64 overflow-y-auto">
              {models.map((m) => (
                <button
                  key={m.id}
                  className={rowClass(shown.lane === 'sub' && shown.provider === provider && shown.model === m.id)}
                  onClick={() => save('sub', provider, m.id)}
                  title={m.id}
                  data-testid={`lane-model-${m.id}`}
                >
                  {m.displayName}
                </button>
              ))}
            </div>
          )}
          {error && (
            <div className="px-3 py-2 text-[10px] text-rexy-danger border-t border-rexy-border"
              data-testid="lane-error">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const LANES_IN_MENU: Lane[] = ['automatic', 'local', 'api', 'sub'];

export default LaneMenu;
